import { useEffect, useRef } from "react";
import { Home, FilePlus2, LayoutList, FileText, Compass, MessageSquare, Mic, ShieldCheck, ShieldAlert, Zap, MoreHorizontal, ChevronDown, Heart, UserRound } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "../ui/dropdown-menu";

export const NAV = [
  { id: "home", label: "Home", short: "Home", icon: Home },
  { id: "profile", label: "My Profile", short: "Profile", icon: UserRound },
  { id: "studio", label: "CV Studio", short: "CV Studio", icon: FilePlus2 },
  { id: "tracker", label: "Applications", short: "Applications", icon: LayoutList },
  { id: "mycv", label: "My CV & Evidence", short: "My CV", icon: FileText },
  { id: "career", label: "Career Journey", short: "Career", icon: Compass },
];
export const MORE = [
  { id: "community", label: "Community", icon: MessageSquare },
  { id: "voice", label: "Refine by Voice", icon: Mic },
  { id: "classic", label: "Quick tailor", icon: Zap },
  { id: "privacy", label: "Privacy & Data", icon: ShieldCheck },
];
export const VIEWS = [...NAV, ...MORE].map((n) => n.id);

const sideCls = (on) => `relative w-full flex items-center gap-3 px-3 py-2.5 min-h-[42px] rounded-lg text-[15px] font-medium text-left transition-colors duration-200 ${on ? "bg-brand-soft text-brand-text" : "text-ink/75 hover:bg-subtle hover:text-ink"}`;
const pillCls = (on) => `flex items-center gap-2 px-3.5 py-2 min-h-[40px] rounded-lg text-sm font-medium whitespace-nowrap shrink-0 transition-colors ${on ? "bg-brand-soft text-brand-text" : "text-ink/75 hover:bg-subtle"}`;

function MoreMenu({ view, onGo, isAdmin, testid, cls, label, side, align, onSupport }) {
  const on = MORE.some((m) => m.id === view);
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger data-testid={testid} className={cls(on)} aria-current={on ? "page" : undefined}>
        <MoreHorizontal className="w-[18px] h-[18px]" aria-hidden="true" /> {label}
        <ChevronDown className="w-4 h-4 ml-auto opacity-60" aria-hidden="true" />
      </DropdownMenuTrigger>
      <DropdownMenuContent side={side} align={align} className="w-60 bg-surface border-line text-ink rounded-xl p-1.5 shadow-float">
        <DropdownMenuLabel className="text-xs text-faint font-semibold uppercase tracking-wide">More tools</DropdownMenuLabel>
        {MORE.map((m) => (
          <DropdownMenuItem key={m.id} data-testid={`${testid}-${m.id}`} onSelect={() => onGo(m.id)}
            className={`gap-2.5 px-2.5 py-2 rounded-lg text-[15px] cursor-pointer focus:bg-subtle ${view === m.id ? "text-brand-text bg-brand-soft" : ""}`}>
            <m.icon className="w-4 h-4" aria-hidden="true" /> {m.label}
          </DropdownMenuItem>
        ))}
        {onSupport && (
          <DropdownMenuItem data-testid={`${testid}-support`} onSelect={onSupport} className="gap-2.5 px-2.5 py-2 rounded-lg text-[15px] cursor-pointer focus:bg-subtle">
            <Heart className="w-4 h-4 text-brand" aria-hidden="true" /> Support us
          </DropdownMenuItem>
        )}
        {isAdmin && (
          <>
            <DropdownMenuSeparator className="bg-line" />
            <DropdownMenuItem asChild className="gap-2.5 px-2.5 py-2 rounded-lg text-[15px] text-warning cursor-pointer focus:bg-subtle">
              <a href="/admin/scam-reports" data-testid={`${testid}-admin-scam`}><ShieldAlert className="w-4 h-4" aria-hidden="true" /> Scam reports</a>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function SideNav({ view, onGo, isAdmin }) {
  return (
    <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto" aria-label="Dashboard">
      {NAV.map((n) => (
        <button key={n.id} data-testid={`nav-${n.id}`} onClick={() => onGo(n.id)} aria-current={view === n.id ? "page" : undefined} className={sideCls(view === n.id)}>
          {view === n.id && <span className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full bg-brand" aria-hidden="true" />}
          <n.icon className="w-[18px] h-[18px]" aria-hidden="true" /> {n.label}
        </button>
      ))}
      <div className="pt-2 mt-2 border-t border-line">
        <MoreMenu view={view} onGo={onGo} isAdmin={isAdmin} testid="nav-more" cls={sideCls} label="More tools" side="right" align="start" />
      </div>
    </nav>
  );
}

export function MobileNav({ view, onGo, isAdmin, onSupport }) {
  const ref = useRef(null);
  useEffect(() => {
    const row = ref.current;
    const el = row?.querySelector('[aria-current="page"]');
    if (row && el) row.scrollTo({ left: el.offsetLeft - (row.clientWidth - el.offsetWidth) / 2, behavior: "smooth" });
  }, [view]);
  return (
    <nav ref={ref} className="md:hidden flex gap-1.5 px-3 py-2.5 border-b border-line bg-surface overflow-x-auto scroll-row" aria-label="Dashboard">
      {NAV.map((n) => (
        <button key={n.id} data-testid={`nav-mobile-${n.id}`} aria-current={view === n.id ? "page" : undefined} className={pillCls(view === n.id)}
          onClick={() => onGo(n.id)}>
          <n.icon className="w-4 h-4" aria-hidden="true" /> {n.short}
        </button>
      ))}
      <MoreMenu view={view} onGo={onGo} isAdmin={isAdmin} onSupport={onSupport} testid="nav-mobile-more" cls={pillCls} label="More" side="bottom" align="end" />
    </nav>
  );
}
